import React, { useState } from "react";
import { X, Image as ImageIcon, FileText, Table, Presentation, File as FileIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import styles from "./UploadedFilesDisplay.module.css";
import { FileAttachment } from "../UserChatMessage/FileAttachment";
import { SessionAttachment } from "../../types/uploads";
import { ImageViewer } from "../ImageViewer/ImageViewer";

interface Props {
    files: SessionAttachment[];
    onRemoveFile: (id: string) => void;
}

export const UploadedFilesDisplay: React.FC<Props> = ({ files, onRemoveFile }) => {
    const { t } = useTranslation();
    const [viewerOpen, setViewerOpen] = useState(false);
    const [selectedImage, setSelectedImage] = useState<{ src: string; name: string } | null>(null);

    const isImageFile = (filename: string) => {
        const imageExtensions = ['.png', '.jpg', '.jpeg', '.bmp', '.heic', '.tiff', '.gif', '.webp'];
        const extension = filename.substring(filename.lastIndexOf('.')).toLowerCase();
        return imageExtensions.includes(extension);
    };

    const handleImageClick = (src: string, name: string) => {
        setSelectedImage({ src, name });
        setViewerOpen(true);
    };

    const handleCloseViewer = () => {
        setViewerOpen(false);
        setSelectedImage(null);
    };

    const getFileTypeInfo = (filename: string): { icon: JSX.Element; label: string; color: string } => {
        const extension = filename.substring(filename.lastIndexOf('.')).toLowerCase();

        switch (extension) {
            case '.pdf':
                return {
                    icon: <FileText size={40} />,
                    label: 'PDF',
                    color: '#DC2626' // Red
                };
            case '.xlsx':
            case '.xls':
            case '.csv':
                return {
                    icon: <Table size={40} />,
                    label: extension === '.csv' ? 'CSV' : 'Excel',
                    color: '#16A34A' // Green
                };
            case '.docx':
            case '.doc':
                return {
                    icon: <FileText size={40} />,
                    label: 'Word',
                    color: '#2563EB' // Blue
                };
            case '.pptx':
            case '.ppt':
                return {
                    icon: <Presentation size={40} />,
                    label: 'PowerPoint',
                    color: '#EA580C' // Orange
                };
            case '.txt':
            case '.md':
                return {
                    icon: <FileText size={40} />,
                    label: 'Text',
                    color: '#6B7280' // Grey
                };
            case '.json':
                return {
                    icon: <FileText size={40} />,
                    label: 'JSON',
                    color: '#6B7280' // Grey
                };
            default:
                return {
                    icon: <FileIcon size={40} />,
                    label: 'File',
                    color: '#6B7280' // Grey
                };
        }
    };

    if (files.length === 0) {
        return null;
    }

    return (
        <>
            <div className={styles.filesContainer}>
                <div className={styles.filesGrid}>
                    {files.map(file => {
                        const isImage = isImageFile(file.name);
                        const previewSrc = file.dataUrl ?? file.sasUrl ?? file.blobUrl;

                        return isImage ? (
                            <div key={file.id} className={styles.imageThumbnailItem}>
                                <div
                                    className={styles.thumbnailWrapper}
                                    onClick={previewSrc ? () => handleImageClick(previewSrc, file.name) : undefined}
                                    style={{ cursor: previewSrc ? 'pointer' : 'default' }}
                                >
                                    {previewSrc ? (
                                        <img
                                            src={previewSrc}
                                            alt={file.name}
                                            className={styles.thumbnailImage}
                                            onError={(e) => {
                                                e.currentTarget.style.display = 'none';
                                                const fallback = e.currentTarget.nextElementSibling;
                                                if (fallback) {
                                                    (fallback as HTMLElement).style.display = 'flex';
                                                }
                                            }}
                                        />
                                    ) : null}
                                    <div className={styles.imageFallback} style={{ display: previewSrc ? 'none' : 'flex' }}>
                                        <ImageIcon size={40} />
                                    </div>
                                    <button
                                        className={styles.deleteOverlay}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onRemoveFile(file.id);
                                        }}
                                        aria-label={t("upload.deleteFile")}
                                        type="button"
                                    >
                                        <X size={18} />
                                    </button>
                                </div>
                                <span className={styles.imageName} title={file.name}>
                                    {file.name}
                                </span>
                            </div>
                        ) : (
                            <div key={file.id} className={styles.documentThumbnailItem}>
                                <div className={styles.documentThumbnailWrapper}>
                                    <div
                                        className={styles.documentIconContainer}
                                        style={{ color: getFileTypeInfo(file.name).color }}
                                    >
                                        {getFileTypeInfo(file.name).icon}
                                    </div>
                                    <div className={styles.documentMetadata}>
                                        <span className={styles.documentFileName} title={file.name}>
                                            {file.name}
                                        </span>
                                        <span className={styles.documentFileType}>
                                            {getFileTypeInfo(file.name).label}
                                        </span>
                                    </div>
                                    <button
                                        className={styles.deleteOverlayDocument}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onRemoveFile(file.id);
                                        }}
                                        aria-label={t("upload.deleteFile")}
                                        type="button"
                                    >
                                        <X size={18} />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
            {selectedImage && (
                <ImageViewer
                    src={selectedImage.src}
                    alt={selectedImage.name}
                    isOpen={viewerOpen}
                    onClose={handleCloseViewer}
                />
            )}
        </>
    );
};


